import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  V2Card,
  V2CardHeader,
  V2Badge,
  V2DataGrid,
  V2EmptyState,
  Icon,
  LoadingState,
  StatePanel,
  type SakhtarIconName,
  type V2GridColumn,
} from '@sakhtar/design-system/react';
import { BarChart } from '../../lib/charts/index.js';
import { useApiGet } from '../../lib/useApiGet.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import { HubKpi, HubKpiGrid } from '../../components/hub/HubKpi.js';
import type {
  RationalQuantity,
  ReservationListResponse,
  StockBalanceRow,
  StockBalancesResponse,
} from '../../lib/types/inventory.js';
import type { MovementListItem, MovementListResponse } from '../../lib/types/inventoryMovements.js';
import type { MasterDataListResponse } from '../../lib/types/masterData.js';
import './hub.css';
import './inventory.css';
import {
  MOVEMENT_STATUS_META,
  formatQuantity,
  isPositiveQuantity,
  metaOf,
} from './inventoryDisplay.js';
import { HubYarCard } from '../../shell/yar/HubYarCard.js';

/** Mirrors GET .../inventory/reports/stock-by-warehouse —
 * apps/api/src/inventory/reports.controller.ts. Server-aggregated per
 * warehouse — never a client-side group-by of the raw balances rows. */
interface StockByWarehouseRow {
  readonly warehouseRef: string;
  readonly warehouseName: string;
  readonly onHand: RationalQuantity;
  readonly reserved: RationalQuantity;
  readonly blocked: RationalQuantity;
  readonly available: RationalQuantity;
}
interface StockByWarehouseResponse {
  readonly authority: 'operational';
  readonly warehouses: readonly StockByWarehouseRow[];
}

/** `limit` the stock-balances endpoint accepts at most
 * (inventory.controller.ts: `Math.min(…, 100)`). Without it the API returns 50. */
const BALANCES_LIMIT = 100;
/** The reservations endpoint has a fixed `LIMIT 100`. */
const RESERVATIONS_LIMIT = 100;
const MOVEMENTS_PER_TYPE = 5;
const TABLE_ROWS = 8;
const MOVEMENTS_SHOWN = 5;
const RESERVATIONS_SHOWN = 5;

/** Approximate float for a chart axis / segment strip only — one already-
 * aggregated value converted once, never summed across rows first (AGENTS.md
 * §8's integer-exactness rule targets authoritative money/quantity, not a
 * visual axis; formatQuantity in inventoryDisplay stays the exact display path). */
function rationalToApproxNumber(q: RationalQuantity): number {
  const denominator = Number(q.denominator);
  return denominator === 0 ? 0 : Number(q.numerator) / denominator;
}

const fa = (n: number) => n.toLocaleString('fa-IR');

const MOVEMENT_META: Record<
  string,
  { readonly label: string; readonly icon: SakhtarIconName; readonly chip: string }
> = {
  receipt: { label: 'رسید', icon: 'arrowDown', chip: 'success' },
  issue: { label: 'خروج', icon: 'arrowUp', chip: 'info' },
  transfer: { label: 'انتقال', icon: 'box', chip: 'warn' },
  count_adjustment: { label: 'تعدیل شمارش', icon: 'layers', chip: 'neutral' },
};

const RESERVATION_STATUS_LABEL = { active: 'فعال', released: 'آزادشده', consumed: 'مصرف‌شده' };

type StockRow = StockBalanceRow & { readonly name: string | null };

/**
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Cloud ERP v2.dc.html — frame `/t/kimia/inventory`, section `isInv`
 *   (overview tab: KPI row, «موجودی کالا — کالاهای نیازمند توجه» table beside
 *   «حرکات اخیر»). Legacy: Sakhtar Inventory.dc.html §01.
 * archetype: MODULE_HUB
 * v2 status: aligned — KPI row (HubKpi, hero + tinted tiles), module-tinted
 *   V2CardHeader cards, V2DataGrid stock table with danger-dot rows, icon-chip
 *   movement list. Layout is two columns (table | side column), wrapping to a
 *   single column on phones.
 * Documented deviations (KIT_CONFORMANT — the design assumes data the backend
 * does not have; nothing is faked, the tile/section is omitted):
 *   - «ارزش موجودی» hero (۸٫۶ میلیارد): no inventory valuation endpoint (the
 *     stock-balances/stock-by-warehouse responses carry no Rial value; only a
 *     single posted movement's finance handoff has values). The hero is the
 *     real count of warehouses holding stock instead (stock-by-warehouse).
 *   - «رسید در انتظار»: every receipt in GET .../inventory/receipts is
 *     `posted` (no pending/draft receipt state, no PO link) — tile omitted.
 *   - «انبارگردانی جاری»: no stocktake/count-session endpoint exists — card
 *     omitted.
 *   - «گروه»/«واحد»/«نقطهٔ سفارش»/«پوشش»/«ارزش»/«آخرین حرکت» columns and the
 *     «زیر نقطهٔ سفارش» legend: no such fields (no reorder point, valuation,
 *     UoM or per-product last movement) — see StockPage.tsx's citation. The
 *     table's footer shows a row count only; quantities of different products
 *     are never summed (mixed units).
 *   - «کالای بحرانی» / table rows / «رزرو فعال» are computed from the most
 *     recent ≤100 stock-balance rows and ≤100 reservations the endpoints
 *     return (no total count is exposed); the tile foot says so when capped.
 *   - «کالا» (name) column uses GET .../master-data/products and is hidden
 *     when the viewer cannot read products.
 *   - Retained real sections not drawn in the reference: «رزروهای فعال» and the
 *     by-warehouse bar chart (FE-INV-2). The reservation-status donut
 *     (FE-INV-3) was dropped — the reservation tile carries the same signal.
 * Endpoints: GET .../inventory/stock-balances?limit=100, /reservations,
 *   /reports/stock-by-warehouse, /receipts|issues|transfers|adjustments
 *   (?limit=5 each, merged by createdAt), GET .../master-data/products.
 *   A 403 on a section hides that section (never a fake zero).
 * state reference: ready · empty · permission_denied · network_error
 */
export function InventoryHubPage() {
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const api = `${base}/api/v1`;

  const balances = useApiGet<StockBalancesResponse>(
    slug ? `${api}/inventory/stock-balances?limit=${BALANCES_LIMIT}` : null,
    [slug],
  );
  const reservations = useApiGet<ReservationListResponse>(
    slug ? `${api}/inventory/reservations` : null,
    [slug],
  );
  const stockByWarehouse = useApiGet<StockByWarehouseResponse>(
    slug ? `${api}/inventory/reports/stock-by-warehouse` : null,
    [slug],
  );
  const products = useApiGet<MasterDataListResponse>(slug ? `${api}/master-data/products` : null, [
    slug,
  ]);
  const receipts = useApiGet<MovementListResponse>(
    slug ? `${api}/inventory/receipts?limit=${MOVEMENTS_PER_TYPE}` : null,
    [slug],
  );
  const issues = useApiGet<MovementListResponse>(
    slug ? `${api}/inventory/issues?limit=${MOVEMENTS_PER_TYPE}` : null,
    [slug],
  );
  const transfers = useApiGet<MovementListResponse>(
    slug ? `${api}/inventory/transfers?limit=${MOVEMENTS_PER_TYPE}` : null,
    [slug],
  );
  const adjustments = useApiGet<MovementListResponse>(
    slug ? `${api}/inventory/adjustments?limit=${MOVEMENTS_PER_TYPE}` : null,
    [slug],
  );

  const productNameByCode = useMemo(() => {
    const map = new Map<string, string>();
    if (products.status === 'ready') {
      for (const p of products.data.items) {
        if (typeof p.code === 'string' && typeof p.name === 'string') map.set(p.code, p.name);
      }
    }
    return map;
  }, [products]);

  /** Newest posted movements across the four types; null while none is readable. */
  const recentMovements = useMemo<readonly MovementListItem[] | null>(() => {
    const lists = [receipts, issues, transfers, adjustments];
    const readable = lists.filter((l) => l.status === 'ready');
    if (readable.length === 0) return null;
    return readable
      .flatMap((l) => (l.status === 'ready' ? l.data.movements : []))
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
      .slice(0, MOVEMENTS_SHOWN);
  }, [receipts, issues, transfers, adjustments]);
  const movementsLoading = [receipts, issues, transfers, adjustments].some(
    (l) => l.status === 'loading',
  );

  if (!slug) return null;

  const balanceRows = balances.status === 'ready' ? balances.data.balances : [];
  const zeroAvailableCount = balanceRows.filter((b) => !isPositiveQuantity(b.available)).length;
  const balancesCapped = balanceRows.length >= BALANCES_LIMIT;
  // Attention first (zero usable stock), then the most recently updated rows.
  const stockRows: StockRow[] = [
    ...balanceRows.filter((b) => !isPositiveQuantity(b.available)),
    ...balanceRows.filter((b) => !!isPositiveQuantity(b.available)),
  ]
    .slice(0, TABLE_ROWS)
    .map((b) => ({
      ...b,
      name: b.productCode ? (productNameByCode.get(b.productCode) ?? null) : null,
    }));

  const allReservations = reservations.status === 'ready' ? reservations.data.reservations : [];
  const activeReservations = allReservations.filter((r) => r.status === 'active');
  const reservationsCapped = allReservations.length >= RESERVATIONS_LIMIT;

  const warehouses = stockByWarehouse.status === 'ready' ? stockByWarehouse.data.warehouses : [];

  const rowKey = (row: StockRow) =>
    `${row.productId}:${row.warehouseId}:${row.warehouseLocationId}:${row.lotId ?? ''}:${row.serialId ?? ''}`;

  const columns: V2GridColumn<StockRow>[] = [
    {
      key: 'code',
      header: 'کد',
      width: 104,
      render: (row) => <span className="ltr sk-num inv-code">{row.productCode ?? '—'}</span>,
    },
    ...(products.status === 'ready'
      ? [
          {
            key: 'name',
            header: 'کالا',
            width: 150,
            wrap: true,
            render: (row: StockRow) => row.name ?? '—',
          } satisfies V2GridColumn<StockRow>,
        ]
      : []),
    { key: 'warehouse', header: 'انبار', render: (row) => row.warehouseCode ?? '—' },
    {
      key: 'onHand',
      header: 'موجودی واقعی',
      align: 'end',
      emphasis: 'group',
      render: (row) => <span className="sk-num">{formatQuantity(row.onHand)}</span>,
    },
    {
      key: 'reserved',
      header: 'رزرو شده',
      align: 'end',
      emphasis: 'group',
      render: (row) => <span className="sk-num">{formatQuantity(row.reserved)}</span>,
    },
    {
      key: 'blocked',
      header: 'مسدود',
      align: 'end',
      emphasis: 'group',
      muted: true,
      render: (row) => <span className="sk-num">{formatQuantity(row.blocked)}</span>,
    },
    {
      key: 'available',
      header: 'قابل استفاده',
      align: 'end',
      emphasis: 'key',
      render: (row) => (
        <span
          className={`sk-num inv-strong ${isPositiveQuantity(row.available) ? '' : 'inv-tone-down'}`}
        >
          {formatQuantity(row.available)}
        </span>
      ),
    },
  ];

  return (
    <div className="inv-hub">
      <HubYarCard moduleId="inventory" />
      {balances.status === 'loading' ? <LoadingState /> : null}
      {balances.status === 'error' ? (
        <StatePanel
          variant={balances.error.status === 403 ? 'permission_denied' : 'network_error'}
          title={balances.error.status === 403 ? 'دسترسی ندارید' : 'موجودی قابل نمایش نیست'}
        />
      ) : null}

      {balances.status === 'ready' ? (
        <HubKpiGrid accent="inventory">
          {warehouses.length > 0 ? (
            <HubKpi
              hero
              label="انبارهای دارای موجودی"
              icon="inventory"
              value={fa(warehouses.length)}
              foot={`${fa(warehouses.length)} انبار فعال`}
              weights={warehouses.slice(0, 6).map((w) => rationalToApproxNumber(w.onHand))}
            />
          ) : (
            <HubKpi
              hero
              label="ردیف موجودی"
              icon="inventory"
              value={fa(balanceRows.length)}
              foot={balancesCapped ? `از ${fa(BALANCES_LIMIT)} ردیف اخیر` : undefined}
            />
          )}
          {reservations.status === 'ready' ? (
            <HubKpi
              label="رزرو فعال"
              value={fa(activeReservations.length)}
              href={`${base}/inventory/reservations`}
              foot={
                reservationsCapped
                  ? `از ${fa(RESERVATIONS_LIMIT)} رزرو اخیر`
                  : `از ${fa(allReservations.length)} رزرو`
              }
              ticks={{ total: activeReservations.length, hot: 0 }}
            />
          ) : null}
          <HubKpi
            label="کالای بحرانی"
            value={fa(zeroAvailableCount)}
            tone={zeroAvailableCount > 0 ? 'danger' : undefined}
            foot={
              zeroAvailableCount > 0
                ? 'قابل استفاده صفر'
                : balancesCapped
                  ? `در ${fa(BALANCES_LIMIT)} ردیف اخیر`
                  : 'همه قابل استفاده‌اند'
            }
            footTone={zeroAvailableCount > 0 ? 'danger' : undefined}
          />
        </HubKpiGrid>
      ) : null}

      {balances.status === 'ready' ? (
        <div className="inv-hub__cols">
          <div className="inv-hub__main">
            <V2Card>
              <V2CardHeader
                icon="box"
                title="موجودی کالا"
                subtitle="کالاهای نیازمند توجه"
                actions={
                  <Link
                    to={`${base}/inventory/stock`}
                    className="sk-btn sk-btn--secondary sk-btn--sm inv-btn-link"
                  >
                    <Icon name="chevronLeft" size="sm" />
                    <span>نمای کامل موجودی</span>
                  </Link>
                }
              />
              <V2DataGrid
                bare
                densitySwitch={false}
                pageSize={0}
                maxHeight="none"
                columns={columns}
                rows={stockRows}
                rowKey={rowKey}
                rowTone={(row) => (!isPositiveQuantity(row.available) ? 'danger' : undefined)}
                footerLabel={`${fa(stockRows.length)} از ${fa(balanceRows.length)} ردیف`}
                legend={[{ tone: 'danger', label: 'قابل استفاده صفر' }]}
                emptyState={
                  <V2EmptyState
                    icon="inventory"
                    title="هنوز موجودی ثبت نشده"
                    description="پس از اولین رسید انبار، موجودی کالا اینجا نمایش داده می‌شود."
                    action={
                      <Link
                        to={`${base}/inventory/receipts`}
                        className="sk-btn sk-btn--secondary sk-btn--sm inv-btn-link"
                      >
                        <span>ثبت رسید</span>
                      </Link>
                    }
                  />
                }
              />
            </V2Card>
          </div>

          <div className="inv-hub__side">
            {recentMovements !== null || movementsLoading ? (
              <V2Card>
                <V2CardHeader
                  icon="box"
                  title="حرکات اخیر"
                  actions={
                    <Link
                      to={`${base}/inventory/receipts`}
                      className="sk-btn sk-btn--ghost sk-btn--sm inv-btn-link"
                    >
                      <span>همه</span>
                    </Link>
                  }
                />
                {recentMovements === null ? (
                  <LoadingState />
                ) : recentMovements.length === 0 ? (
                  <V2EmptyState icon="inventory" compact title="حرکتی ثبت نشده" />
                ) : (
                  <div>
                    {recentMovements.map((m) => {
                      const meta = MOVEMENT_META[m.movementType] ?? {
                        label: m.movementType,
                        icon: 'box' as const,
                        chip: 'neutral',
                      };
                      return (
                        <div key={m.movementId} className="inv-hub__row">
                          <span
                            className={`inv-hub__row-chip inv-hub__row-chip--${meta.chip}`}
                            aria-hidden="true"
                          >
                            <Icon name={meta.icon} size="sm" />
                          </span>
                          <div className="inv-hub__row-main">
                            <span>
                              {meta.label}{' '}
                              {m.status === 'posted' ? (
                                <Link
                                  to={`${base}/inventory/movements/${m.movementId}`}
                                  className="ltr sk-num inv-link"
                                >
                                  {m.movementNumber}
                                </Link>
                              ) : (
                                <span className="ltr sk-num">{m.movementNumber}</span>
                              )}
                            </span>
                            <span className="inv-hub__row-sub sk-num">
                              {displayJalaliDate(m.businessDate)}
                            </span>
                          </div>
                          <V2Badge tone={metaOf(MOVEMENT_STATUS_META, m.status).tone}>
                            {metaOf(MOVEMENT_STATUS_META, m.status).label}
                          </V2Badge>
                        </div>
                      );
                    })}
                  </div>
                )}
              </V2Card>
            ) : null}

            {reservations.status === 'ready' ? (
              <V2Card>
                <V2CardHeader
                  icon="layers"
                  title="رزروهای فعال"
                  actions={
                    <Link
                      to={`${base}/inventory/reservations`}
                      className="sk-btn sk-btn--ghost sk-btn--sm inv-btn-link"
                    >
                      <span>همه</span>
                    </Link>
                  }
                />
                {activeReservations.length === 0 ? (
                  <V2EmptyState icon="info" compact title="رزرو فعالی ثبت نشده" />
                ) : (
                  <div>
                    {activeReservations.slice(0, RESERVATIONS_SHOWN).map((r) => (
                      <div key={r.reservationId} className="inv-hub__row">
                        <div className="inv-hub__row-main">
                          <span className="ltr">{r.sourceReference}</span>
                          <span className="inv-hub__row-sub sk-num">
                            {formatQuantity(r.quantity)}
                          </span>
                        </div>
                        <V2Badge tone="success">{RESERVATION_STATUS_LABEL[r.status]}</V2Badge>
                      </div>
                    ))}
                  </div>
                )}
              </V2Card>
            ) : null}

            {warehouses.length > 0 ? (
              <V2Card>
                <V2CardHeader icon="inventory" title="موجودی قابل استفاده به تفکیک انبار" />
                <div className="inv-hub__chart">
                  <BarChart
                    ariaLabel="موجودی قابل استفاده به تفکیک انبار"
                    data={warehouses.map((w) => ({
                      label: w.warehouseName,
                      value: rationalToApproxNumber(w.available),
                    }))}
                  />
                </div>
              </V2Card>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
